"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.newValueFieldState = newValueFieldState;
const is_plain_object_1 = require("is-plain-object");
const mobx_1 = require("mobx");
const proxies_1 = require("../proxies");
const rules_1 = require("../rules");
const utils_1 = require("../utils");
function newValueFieldState(parentCopy, parentInstance, parentState, key, rules, isIdKey, isDeleteKey, isReadOnlyKey, computed, readOnly, strictOrder, maybeAutoSave) {
    // Because we read/write the value directly back into parentInstance[key],
    // which itself is not a proxy, we use this as our "value changed" trigger.
    const _tick = (0, mobx_1.observable)({ value: 1 });
    const _originalValueTick = (0, mobx_1.observable)({ value: 1 });
    const field = {
        key: key,
        touched: false,
        /** Current readOnly value. */
        _readOnly: readOnly || false,
        _loading: false,
        _kind: "value",
        // Expose so computed can be skipped in changedValue
        _computed: computed,
        _focused: false,
        _isIdKey: isIdKey,
        _isDeleteKey: isDeleteKey,
        _isReadOnlyKey: isReadOnlyKey,
        rules,
        get value() {
            // If we're wrapping a mobx store, then we'll get reactivity from parentInstance[key]
            const value = _tick.value > 0 ? parentInstance[key] : (0, utils_1.fail)();
            // Re-create the `keepNull` logic on sets but for our initial read where our
            // originalValue is null (empty) but we want to expose it as undefined for
            // consistency of "empty-ness" to our UI components.
            return value === null && (0, utils_1.isEmpty)(parentCopy[key]) ? undefined : value;
        },
        set value(v) {
            this.set(v);
        },
        get focused() {
            return this._focused;
        },
        get dirty() {
            return !(0, utils_1.areEqual)(this.originalValue, this.value, strictOrder);
        },
        /** Returns whether this field is readOnly, although if our parent is readOnly then it trumps. */
        get readOnly() {
            return parentState().readOnly || this._readOnly;
        },
        /** Sets the field readOnly, but `loading` will still be or-d with the parent's readOnly state. */
        set readOnly(readOnly) {
            this._readOnly = readOnly;
        },
        /** Returns whether this field is loading, or if our parent is loading. */
        get loading() {
            return parentState().loading || this._loading;
        },
        /** Sets the field loading, but `loading` will still be or-d with the parent's loading state. */
        set loading(loading) {
            this._loading = loading;
        },
        // For primitive fields, the changed value is just the value.
        get changedValue() {
            // Usually if we see a field being unset, we set `parent[key] = null`, but if we're wrapping
            // a mobx observable object, it might have changed to `undefined` without us being able to
            // tell it to be `null` (and potentially break the type contract of `string | undefined`).
            // So detect un-set-ness here and return `null`.
            if (this.value === undefined && this.originalValue !== undefined)
                return null;
            return this.value;
        },
        get valid() {
            const opts = { value: this.value, key: key, originalValue: this.originalValue, object: parentState() };
            return this.rules.every((r) => r(opts) === undefined);
        },
        get errors() {
            const opts = { value: this.value, key: key, originalValue: this.originalValue, object: parentState() };
            return this.rules.map((r) => r(opts)).filter(utils_1.isNotUndefined);
        },
        get required() {
            return this.rules.some((rule) => rule === rules_1.required);
        },
        get isNewEntity() {
            return parentState().isNewEntity;
        },
        focus() {
            this._focused = true;
        },
        blur() {
            this.maybeAutoSave();
            this._focused = false;
        },
        maybeAutoSave() {
            // Now that the user is done editing the field (note maybe w/o blurring, i.e. if they hit enter), we sneak in some trim logic
            this.maybeTrim();
            // touched is readonly, but we're allowed to change it
            this.touched = true;
            maybeAutoSave();
        },
        set(value, opts = {}) {
            if (this.readOnly && !opts.resetting && !opts.refreshing) {
                throw new Error(`${String(key)} is currently readOnly`);
            }
            const isAckingUnset = this.value === null && (value === null || value === undefined);
            if (opts.refreshing && this.dirty && this.value !== value && !isAckingUnset) {
                // Ignore incoming values if we have changes (this.dirty) unless our latest change (this.value)
                // matches the incoming value (value), b/c if it does we should accept it and reset originalValue
                // so that we're no longer dirty.
                return;
            }
            else if (computed && (opts.resetting || opts.refreshing)) {
                // Computeds can't be either reset or refreshed
                return;
            }
            // If the user has deleted/emptied a value that was originally set, keep it as `null`
            // so that our partial update to the backend correctly unsets it.
            const keepNull = !(0, utils_1.isEmpty)(this.originalValue) && (0, utils_1.isEmpty)(value) && !opts.refreshing;
            // If a list of primitives was originally undefined, coerce `[]` to `undefined`
            const coerceEmptyList = value && value instanceof Array && value.length === 0 && (0, utils_1.isEmpty)(this.originalValue);
            const newValue = keepNull ? null : (0, utils_1.isEmpty)(value) || coerceEmptyList ? undefined : value;
            // Set the value on our parent object
            const changed = !(0, utils_1.areEqual)(newValue, this.value, strictOrder);
            parentInstance[key] = newValue;
            _tick.value++;
            if (opts.refreshing) {
                this.originalValue = newValue;
            }
            // If we're being set programmatically, i.e. we don't currently have focus,
            // call blur to trigger any auto-saves.
            if (!this._focused && !opts.refreshing && !opts.resetting && this.dirty && changed && opts.autoSave !== false) {
                this.maybeAutoSave();
            }
        },
        adapt(adapter) {
            return adapt(this, adapter);
        },
        revertChanges() {
            if (!computed) {
                this.set(this.originalValue, { resetting: true });
            }
            this.touched = false;
        },
        commitChanges() {
            if ((0, is_plain_object_1.isPlainObject)(this.originalValue)) {
                this.originalValue = (0, mobx_1.toJS)(this.value);
            }
            else {
                this.originalValue = this.value;
            }
            this.touched = false;
        },
        get originalValue() {
            // A dummy check to for reactivity around our non-proxy value
            const value = _originalValueTick.value > -1 ? parentCopy[key] : parentCopy[key];
            // Re-create the `keepNull` logic so that `.value` === `.originalValue`
            return value === null ? undefined : value;
        },
        set originalValue(v) {
            const canSkip = v === undefined && !(key in parentCopy);
            if (!canSkip)
                parentCopy[key] = v;
            _originalValueTick.value++;
        },
        maybeTrim() {
            if (typeof this.value === "string") {
                let newValue = this.value.trim();
                if (newValue === "") {
                    newValue = undefined;
                }
                if (newValue !== this.value) {
                    this.set(newValue);
                }
            }
        },
    };
    // If we're wrapping a mobx observer, watching for external mutations, i.e. from callers not
    // going through our FieldState.set/FieldState.value setters.
    if ((0, mobx_1.isObservable)(parentInstance)) {
        (0, mobx_1.reaction)(() => parentInstance[key], () => {
            // Don't auto-save on maybe-external mutation if our field, or another other field (potentially a computed),
            // is currently focused, because then it's probably just us doing the writes through `Bound...Field` components.
            if (!parentState().focused)
                maybeAutoSave();
        });
    }
    return field;
}
/**
 * Returns a proxy that looks exactly like the original `field`, in terms of valid/touched/errors/etc., but
 * has any methods that use `V` overridden to use be `V2`.
 *
 * Note that `V2` can be a new type, like string -> number, or just a transformation on the same
 * type, i.e. feet -> inches where both are `number`s.
 */
function adapt(field, adapter) {
    return (0, proxies_1.newDelegateProxy)(field, {
        rules: [],
        get value() {
            return adapter.toValue(field.value);
        },
        set value(v) {
            field.value = adapter.fromValue(v);
        },
        set: (v) => {
            field.value = adapter.fromValue(v);
        },
        get changedValue() {
            return this.value;
        },
        adapt(adapter) {
            return adapt(this, adapter);
        },
        get originalValue() {
            return adapter.toValue(field.originalValue);
        },
    });
}
